// src/components/CategoriesPage.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import ThemesDropdown from "./ThemesDropdown";
import Footer from "./Footer";
import "../css/okgather1.webflow.css";
import { trackPageView, setUserIdProperty } from "../utils/tracking";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import { standardUserApi } from "../services/standardUserApi";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({
  publicKey: currentKey,
});

/**
 * CategoriesPage - A dedicated page for unauthenticated users to select a gathering category
 * This page features the ThemesDropdown component prominently
 */
const CategoriesPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userLocationId, setUserLocationId] = useState(null);

  // Check if user is logged in and fetch their location
  useEffect(() => {
    (async () => {
      // A memberstackToken means this browser already completed a real
      // login/signup, so a single transient failure (network blip, SDK not
      // fully settled right after a redirect) shouldn't immediately be
      // treated as "logged out" - retry once before giving up.
      const hasToken = !!localStorage.getItem("memberstackToken");
      for (let attempt = 0; attempt < (hasToken ? 2 : 1); attempt++) {
        try {
          const currentMember = await memberstack.getCurrentMember();
          if (currentMember?.data) {
            navigate("/my-events");
            return;
          }
        } catch (err) {
          console.error("Memberstack error:", err);
        }
        if (hasToken && attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 800));
        }
      }
      setIsLoggedIn(false);
    })();
  }, []);

  // Track page view on mount
  useEffect(() => {
    setUserIdProperty(); // Set user ID if logged in

    const hasReferralCode = localStorage.getItem('referralCode');

    trackPageView('Categories Page', {
      is_logged_in: isLoggedIn,
      has_referral_code: !!hasReferralCode,
      referral_code: hasReferralCode || null
    });

    // Track referral funnel step if user came from referral
    if (hasReferralCode && mixpanel && mixpanel.__loaded) {
      mixpanel.track('Referral Funnel: Categories Viewed', {
        funnel_step: 'categories',
        funnel_step_number: 2,
        referral_code: hasReferralCode,
        has_referral_code: true
      });
    }
  }, [isLoggedIn]);

  return (
    <div className="page-wrapper" style={{ minHeight: "100vh", backgroundColor: "#FAFAFA" }}>
      {/* Top Navigation Banner */}
      <section
        className="nav-banner"
        style={{
          background: "linear-gradient(135deg, #FBFAFF, #F5F2FF)",
          borderBottom: "1px solid rgba(139, 95, 191, 0.08)",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <div className="padding-global">
          <div className="container-large">
            <div
              className="nav-content"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "18px 0",
              }}
            >
              <Link to="/" style={{ textDecoration: 'none' }}>
                <span
                  className="nav-logo"
                  style={{
                    fontSize: "2rem",
                    fontWeight: "800",
                    background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    margin: 0,
                    lineHeight: "44px",
                    display: "inline-block",
                  }}
                >
                  OkGather
                </span>
              </Link>

              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                {isLoggedIn ? (
                  <Link
                    to="/edit-profile"
                    style={{
                      background: "transparent",
                      color: "#8B5FBF",
                      padding: "12px 24px",
                      borderRadius: "25px",
                      textDecoration: "none",
                      fontSize: "0.95rem",
                      fontWeight: "600",
                      border: "2px solid #8B5FBF",
                      transition: "all 0.3s ease",
                    }}
                  >
                    My Profile
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/gathering-matcher"
                      className="nav-signup-button"
                      style={{
                        background: "linear-gradient(135deg, #8B5FBF, #2c2e96)",
                        color: "#fff",
                        padding: "12px 24px",
                        borderRadius: "25px",
                        textDecoration: "none",
                        fontSize: "0.95rem",
                        fontWeight: "600",
                        transition: "all 0.3s ease",
                        boxShadow: "0 2px 10px rgba(139, 95, 191, 0.2)",
                        border: "none",
                      }}
                    >
                      Sign Up
                    </Link>
                    <Link
                      to="/login"
                      className="nav-login-button"
                      style={{
                        background: "transparent",
                        color: "#8B5FBF",
                        padding: "12px 24px",
                        borderRadius: "25px",
                        textDecoration: "none",
                        fontSize: "0.95rem",
                        fontWeight: "600",
                        transition: "all 0.3s ease",
                        border: "2px solid #8B5FBF",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "#8B5FBF";
                        e.target.style.color = "#fff";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#8B5FBF";
                      }}
                    >
                      Login
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section
        style={{
          background: "linear-gradient(135deg, #8B5FBF 0%, #6B46C1 50%, #2c2e96 100%)",
          padding: "40px 20px 50px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <h1
            style={{
              fontSize: "clamp(2rem, 5vw, 3rem)",
              fontWeight: "800",
              color: "#fff",
              marginBottom: "16px",
              lineHeight: "1.2",
            }}
          >
            Browse Gathering Themes
          </h1>
          <p
            style={{
              fontSize: "1.2rem",
              color: "rgba(255, 255, 255, 0.9)",
              maxWidth: "600px",
              margin: "0 auto",
              lineHeight: "1.6",
            }}
          >
            Choose a theme and join a gathering for drinks with new people
          </p>
        </div>
      </section>

      {/* Categories Section */}
      <section
        style={{
          padding: "0 20px 40px",
          marginTop: "-40px",
        }}
      >
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <ThemesDropdown locationId={userLocationId} />
        </div>
      </section>

      {/* How It Works Section */}
      <section
        style={{
          backgroundColor: "#fff",
          padding: "40px 20px",
          borderTop: "1px solid rgba(139, 95, 191, 0.1)",
        }}
      >
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "800",
              background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              marginBottom: "40px",
              textAlign: "center",
            }}
          >
            How It Works
          </h2>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            {[
              { num: "1", title: "Choose a Theme", desc: "Select a gathering type that interests you", icon: "🎯" },
              { num: "2", title: "Create Your Account", desc: "Sign up quickly — just your name, email, and password", icon: "✨" },
              { num: "3", title: "Pick a Date", desc: "Choose from available dates that work for you", icon: "📅" },
              { num: "4", title: "Meet New People", desc: "Show up and enjoy drinks with your group!", icon: "🥳" },
            ].map((step, index, array) => (
              <React.Fragment key={step.num}>
                <div
                  style={{
                    backgroundColor: "#FAFAFA",
                    borderRadius: "16px",
                    padding: "32px 24px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "2.5rem",
                      marginBottom: "12px",
                    }}
                  >
                    {step.icon}
                  </div>
                  <h3
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "700",
                      color: "#333",
                      marginBottom: "8px",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontSize: "0.95rem",
                      color: "#666",
                      margin: 0,
                      lineHeight: "1.5",
                    }}
                  >
                    {step.desc}
                  </p>
                </div>
                {index < array.length - 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "16px 0",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        backgroundColor: "#E9D5FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(139, 95, 191, 0.15)",
                      }}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 5V19M12 19L6 13M12 19L18 13"
                          stroke="#8B5FBF"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CategoriesPage;
